import {applySnapshot, flow, getParent, types} from 'mobx-state-tree';
import DeviceModel from '../models/device-model';
import PositionService from '../services/position-service';
import DeviceService from 'src/services/device-service.js';
import {toJS} from 'mobx';

const DeviceStore = types
  .model('DeviceStore', {
    devices: types.optional(types.array(DeviceModel), []),
    selectedDevice: types.maybeNull(
      types.reference(types.late(() => DeviceModel)),
    ),
    updatedPositions: types.optional(types.boolean, false),
    hasLoaded: types.optional(types.boolean, false),
  })
  .views((self) => ({
    get devicesMap() {
      const map = new Map();
      for (const device of self.devices) {
        map.set(device.id, device);
      }
      return map;
    },
  }))
  .actions((self) => {
    let isLoadDevicesRunning = false;

    const TAMANHO_DO_BLOCO = 250;

    const aplicarPosicoesEmBlocos = (positions) => {
      if (!positions || !positions.length) {
        self.updatePositions([], true);
        return;
      }

      let inicio = 0;
      const proximoBloco = () => {
        if (inicio >= positions.length) return;

        const bloco = positions.slice(inicio, inicio + TAMANHO_DO_BLOCO);
        inicio += TAMANHO_DO_BLOCO;
        const ultimo = inicio >= positions.length;

        self.updatePositions(bloco, ultimo);

        if (!ultimo) setTimeout(proximoBloco, 0);
      };

      proximoBloco();
    };

    return {
      loadDevices: flow(function* () {
        if (self.hasLoaded || isLoadDevicesRunning) return;
        isLoadDevicesRunning = true;

        const deviceService = new DeviceService();
        const positionService = new PositionService();
        const positionsPromise = positionService.list();

        const _devices = yield deviceService.getDevices();
        _devices.sort((a, b) => a.name.localeCompare(b.name));

        const globalStore = getParent(self);
        yield globalStore.customerStore.loadCustomers();
        yield globalStore.group.loadGroups();

        for (const _device of _devices) {
          const customer = globalStore.customerStore.findById(_device.customerId);
          if (!!customer) {
            _device.customerName = customer;
            _device.customerCpfCnpj = customer;
          }

          const ownerCustomer = globalStore.customerStore.findById(_device.ownerCustomerId);
          if (!!ownerCustomer) {
            _device.ownerCustomerName = ownerCustomer;
          }

          const group = globalStore.group.findById(_device.groupId);
          if (!!group) {
            _device.groupName = group;
          }
        }

        applySnapshot(self.devices, _devices);

        const positions = yield positionsPromise;

        self.hasLoaded = true;
        isLoadDevicesRunning = false;

        // As posicoes entram depois que a frota ja esta na tela, em blocos. Cada posicao vira um no
        // da arvore, e com frota grande esse passo custa mais que montar os proprios dispositivos --
        // segurar a tela ate o fim dele atrasava a primeira pintura sem necessidade. Nenhuma posicao
        // deixa de ser aplicada; elas chegam escalonadas, como ja chegam pelo socket depois.
        aplicarPosicoesEmBlocos(positions);
      }),

      add: (device) => self.devices.push(device),

      addAll: (devices) => self.devices.push(...devices),

      // Marcador revogado pode sumir da store (canal devicesRemoved) antes do mapa
      // aposentar o feature visual dele; clicar nesse resto visual chama setSelected
      // com um id que a arvore MST nao tem mais -- apontar a referencia pra ele quebra
      // a leitura de selectedDevice em qualquer lugar (tela branca). So aponta se existe.
      setSelected: (id) => {
        self.selectedDevice = self.devicesMap.has(id) ? id : null;
      },

      updateDevice: ({id, ...data}) => {
        const index = self.devices.findIndex((d) => d.id === id);
        if (index >= 0) {
          self.devices[index].update(data);
        } else if (typeof id === 'number' && typeof data.uniqueId === 'string' && typeof data.name === 'string') {
          self.add({id, ...data});
        }
      },

      removeDevice: (id) => {
        const index = self.devices.findIndex((d) => d.id === id);
        if (index >= 0) {
          // Tirar o device com o array ainda apontando ele como selectedDevice
          // (referencia MST) quebra a resolucao da referencia e derruba a arvore
          // inteira -- limpar antes de splice.
          if (self.selectedDevice && self.selectedDevice.id === id) {
            self.selectedDevice = null;
          }
          self.devices.splice(index, 1);
        }
      },

      updatePosition: (position) => {
        if (position) {
          const index = self.devices.findIndex((d) => d.id === position.deviceId);
          if (index >= 0) {
            self.devices[index].newPosition(position);
            self.devices[index].setCurrentPosition(position);
            // self.updatedPositions = true;
          }
        }
      },

      // `updatedPositions` anuncia que a carga inicial terminou. O mapa plota a frota inteira quando
      // ele vira verdadeiro, e ele nunca volta atras -- entao quem levanta cedo demais condena os
      // dispositivos que ainda nao entraram a ficar sem marcador ate mandarem posicao nova. Por isso
      // o padrao e nao sinalizar: so a carga inicial passa `true`, no ultimo bloco. O socket, que
      // aplica posicao o tempo todo e nao carrega nada, entra pelo padrao.
      updatePositions: (positions, sinalizarCargaInicial = false) => {
        if (positions && positions.length) {
          const porId = self.devicesMap;

          for (const position of positions) {
            const device = porId.get(position.deviceId);
            if (device) {
              device.newPosition(position);
              device.setCurrentPosition(position);
            }
          }
        }

        if (sinalizarCargaInicial) self.updatedPositions = true;
      },

      updateDeviceAccumulator(deviceId, {totalDistance, hours, ignoreOdometerEnabled = false}) {
        const device = self.findById(deviceId);
        if (device) {
          device.updateAccumulator({totalDistance, hours, ignoreOdometerEnabled});
        }
      },

      createDeviceRemote: flow(function* (data, images) {
        const deviceService = new DeviceService();
        const newDevice = yield deviceService.save(data);
        self.add(newDevice);
        if (images && Array.isArray(images) && images.length > 0) {
          yield deviceService.uploadImages(newDevice.id, images);
        }
      }),

      updateDeviceRemote: flow(function* (data, images) {
        const deviceService = new DeviceService();
        const updatedDevice = yield deviceService.update(data);
        self.updateDevice(updatedDevice);

        console.log('__updateDeviceRemote_1', {data, images});
        if (!images || (images && !Array.isArray(images))) return;
        const currentImages = yield self.findById(updatedDevice.id).findImages();
        if (currentImages.length === 0 && images.length === 0) return;

        if (currentImages.length > 0 && images.length === 0) {
          // remove each image
          for (const image of currentImages) {
            const imageNameParts = image.split('/');
            const imageName = imageNameParts[imageNameParts.length - 1];
            yield deviceService.removeImage(updatedDevice.id, imageName);
          }
        } else {
          // update all images
          yield deviceService.uploadImages(updatedDevice.id, images);
        }
      }),
    };
  })
  .views((self) => ({
    get pickNotSelected() {
      return self.devices.filter((d) => d !== self.selectedDevice);
    },

    get pickWithoutOpenSinister() {
        const {sinisterStore} = getParent(self, 1);

        const openedSinisterDevicesId = sinisterStore.pickOpened.map((sinister) => {
            return sinister.deviceId;
        });

        return self.devices.filter((device) =>
            !openedSinisterDevicesId.includes(device.id),
        );
    },

    get list() {
        return self.devices.toJSON();
    },

    // Somente os campos de cadastro. A tela de listagem nao mostra nenhuma coluna de posicao, mas
    // `list` serializa o dispositivo inteiro -- inclusive o buffer de posicoes -- e por isso era
    // recalculada a cada posicao recebida, varias vezes por segundo, com a frota inteira.
    get registrationList() {
        return self.devices.map((device) => ({
            id: device.id,
            uniqueId: device.uniqueId,
            name: device.name,
            attributes: device.attributes,
            category: device.category,
            contact: device.contact,
            disabled: device.disabled,
            shareable: device.shareable,
            mirrorable: device.mirrorable,
            geofenceIds: device.geofenceIds,
            groupId: device.groupId,
            groupName: device.groupName,
            model: device.model,
            phone: device.phone,
            customerId: device.customerId,
            customerName: device.customerName,
            ownerCustomerId: device.ownerCustomerId,
            ownerCustomerName: device.ownerCustomerName,
            customerCpfCnpj: device.customerCpfCnpj,
            createdByUserId: device.createdByUserId,
            createdAt: device.createdAt,
            updatedAt: device.updatedAt,
            customTraceLimit: device.customTraceLimit,
        }));
    },

    // So os campos que as agregacoes do painel leem. `list` serializa cada dispositivo inteiro,
    // inclusive o buffer de posicoes, e isso e a maior parte do custo de montar o painel com frota
    // grande -- os graficos usam apenas contagem por estado, categoria e ultima comunicacao.
    get summaryList() {
        return self.devices.map((device) => ({
            id: device.id,
            disabled: device.disabled,
            status: device.status,
            category: device.category,
            lastUpdate: device.lastUpdate,
        }));
    },

    // Dispositivo inteiro, com telemetria, para quem precisa dele por inteiro em um clique --
    // formulario de odometro e exportacao da planilha.
    snapshotById(id) {
        const device = self.devicesMap.get(id);
        return device ? device.toJSON() : undefined;
    },

    get listAsOptions() {
        return toJS(self.devices.map((device) => ({
            id: device.id,
            title: `${device.uniqueId} - ${device.name}`,
        }))).sort((a, b) => a.title.localeCompare(b.title));
    },

    get listWithoutOpenSinisterAsOptions() {
        return toJS(self.pickWithoutOpenSinister.map((device) => ({
            id: device.id,
            title: `${device.uniqueId} - ${device.name}`,
        }))).sort((a, b) => a.title.localeCompare(b.title));
    },

    hasPosition: (id) => {
      const device = self.findById(id);
      if (!device) return false;

      return device.hasPosition();
    },

    isTag: (id) => {
      const device = self.findById(id);
      if (!device) return false;

      return device.isTag();
    },

    findById: (id) => self.devices.find((e) => e.id === id),

    findPositionedDevices() {
      return self.devices.filter((device) => !!device.positionId && !!device.currentPosition);
    },

    findDevicesWithAlarm() {
      return this.findPositionedDevices().filter((device) => !!device.currentPosition.attributes.alarm);
    },

    get isSomeDeviceWithTraceEnabled() {
      return self.devices.some((device) => device.withTrace);
    },
  }));

export default DeviceStore;
